import { Link } from "wouter";

const features = [
  { k: "Talk to your procurement", b: "Ask in plain English — \"reorder last Tuesday's vegetables\" — the agent calls Zeemart's own endpoints and shows you the draft PO before it commits." },
  { k: "Generative UI, not screens", b: "Every result streams in as live components: KPIs, supplier rows, order cards. The UI adapts to what you asked, not what the menu hardcoded." },
  { k: "Pointer-not-payload", b: "Your session token never lives in the browser. A Cloudflare Worker holds it, the UI carries only an opaque cookie. Closing the tab logs you out, gracefully." },
  { k: "Your old screens, faster", b: "Outlets, suppliers, orders — the original Zeemart workflows are all here, rebuilt on the same APIs, with a third the clicks." },
];

const tiers = [
  {
    name: "Starter",
    price: "Free",
    blurb: "Try the agent against your real data. No credit card.",
    cta: "Start with your Zeemart account",
    href: "/login",
    features: ["1 outlet", "Read-only agent tools", "Generative UI on all pages"],
    accent: false,
  },
  {
    name: "Operator",
    price: "$49/outlet/mo",
    blurb: "The agent places orders, schedules deliveries, chases acks.",
    cta: "Start 14-day trial",
    href: "/login?intent=operator",
    features: ["Up to 5 outlets", "Write-tools (reorder, draft, place)", "Slack + WhatsApp delivery", "Spend guardrails"],
    accent: true,
  },
  {
    name: "Group",
    price: "Talk to us",
    blurb: "Central kitchens, multi-brand, custom workflows.",
    cta: "Book a call",
    href: "mailto:hello@nomni.ai",
    features: ["Unlimited outlets", "Custom agent tools", "SSO + audit log", "Priority support"],
    accent: false,
  },
];

export function Landing() {
  return (
    <main className="bg-app min-h-[100dvh]">
      <header className="px-8 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link href="/" className="flex items-center gap-2 font-display italic text-xl">
          <span className="size-7 rounded-md bg-nomni" />
          Nomni <span className="text-ink-mute text-xs not-italic">Procure</span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <a href="#features" className="text-ink-2 hover:text-ink">How it works</a>
          <a href="#pricing" className="text-ink-2 hover:text-ink">Pricing</a>
          <Link href="/login" className="rounded-full bg-nomni-deep text-white px-4 py-2 text-sm hover:bg-nomni-leaf">
            Sign in
          </Link>
        </nav>
      </header>

      {/* HERO */}
      <section className="px-8 max-w-6xl mx-auto pt-12 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-mint/60 text-nomni-deep px-3 py-1 text-xs uppercase tracking-[0.16em]">
            <span className="size-1.5 rounded-full bg-nomni-leaf animate-pulse" />
            Agent-native rebuild of buyer.zeemart.co
          </div>
          <h1 className="mt-5 text-5xl lg:text-6xl tracking-tight text-ink text-balance">
            Talk to your <span className="font-display italic text-nomni-deep">procurement.</span>
          </h1>
          <p className="mt-5 text-lg text-ink-2 max-w-xl text-pretty">
            Nomni is the agent-native shell over Zeemart's own buyer APIs. Same outlets,
            same suppliers, same orders — but every screen is generative UI you can ask for.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link href="/login" className="rounded-full bg-nomni-deep text-white px-6 py-3 text-base hover:bg-nomni-leaf">
              Sign in with Zeemart
            </Link>
            <a href="#pricing" className="rounded-full ring-1 ring-rule text-ink-2 px-6 py-3 text-base hover:bg-panel">
              See pricing
            </a>
          </div>
          <p className="mt-3 text-xs text-ink-mute">No new account. Your existing Zeemart credentials work.</p>
        </div>
        <div className="rounded-[var(--radius-card)] bg-panel shadow-[var(--shadow-rise)] ring-1 ring-rule overflow-hidden">
          <div className="px-4 py-2 border-b border-rule flex items-center gap-2 bg-cream/40 text-xs text-ink-mute">
            <span className="size-2.5 rounded-full bg-[oklch(0.78_0.18_27)]" />
            <span className="size-2.5 rounded-full bg-[oklch(0.78_0.17_78)]" />
            <span className="size-2.5 rounded-full bg-nomni" />
            <span className="ml-2">chat · nomni · ABCDEF CK Testing</span>
          </div>
          <div className="p-5 space-y-3 text-sm">
            <div className="text-ink-mute">you</div>
            <div className="bg-mint/40 inline-block rounded-2xl px-4 py-2">
              What did I order from Bidfood last week?
            </div>
            <div className="text-ink-mute mt-3">nomni</div>
            <div className="space-y-2">
              <div className="rounded-[var(--radius-card)] ring-1 ring-rule p-3">
                <div className="text-xs uppercase tracking-wider text-ink-mute">Spend · Bidfood · 7d</div>
                <div className="text-3xl tabular-nums mt-1">$2,418.30</div>
                <div className="text-xs text-nomni-leaf mt-1">+12% vs prior week</div>
              </div>
              <div className="rounded-[var(--radius-card)] ring-1 ring-rule p-3 flex items-center justify-between">
                <div>
                  <div className="text-ink font-medium">PO 482190</div>
                  <div className="text-xs text-ink-mute">Mon · 14 line items</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider rounded-full bg-placed-soft text-nomni-deep px-2 py-0.5">placed</span>
              </div>
              <button className="text-xs rounded-full bg-nomni-deep text-white px-3 py-1.5">Reorder all</button>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="px-8 max-w-6xl mx-auto py-12">
        <h2 className="text-xs uppercase tracking-[0.2em] text-ink-mute">How it works</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {features.map((f) => (
            <div key={f.k} className="rounded-[var(--radius-card)] bg-panel p-6 ring-1 ring-rule">
              <div className="text-ink font-medium text-lg">{f.k}</div>
              <p className="text-ink-2 mt-2 text-pretty">{f.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-8 max-w-6xl mx-auto py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] text-ink-mute">Pricing</h2>
            <h3 className="text-3xl tracking-tight mt-2">Subscribe per outlet, cancel anytime.</h3>
            <p className="sr-only">subscription pricing options for nomni procure</p>
          </div>
          <p className="text-ink-mute text-sm max-w-md">Every tier rides on your existing Zeemart account. No data migration. Turn it off, you go back to the old buyer.zeemart.co the next morning.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={`rounded-[var(--radius-card)] p-6 flex flex-col gap-5 ${
                t.accent
                  ? "bg-nomni-deep text-cream ring-2 ring-nomni shadow-[var(--shadow-rise)]"
                  : "bg-panel text-ink ring-1 ring-rule"
              }`}
            >
              <div>
                <div className={`text-xs uppercase tracking-[0.18em] ${t.accent ? "text-nomni/90" : "text-ink-mute"}`}>{t.name}</div>
                <div className="text-3xl tracking-tight mt-1">{t.price}</div>
                <p className={`mt-2 text-sm ${t.accent ? "text-cream/80" : "text-ink-2"}`}>{t.blurb}</p>
              </div>
              <ul className="text-sm space-y-2 flex-1">
                {t.features.map((feat) => (
                  <li key={feat} className="flex gap-2">
                    <span className={t.accent ? "text-nomni" : "text-nomni-leaf"}>✓</span>
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              <a
                href={t.href}
                className={`rounded-full text-center py-3 font-medium ${
                  t.accent ? "bg-nomni text-nomni-deep hover:brightness-105" : "bg-ink text-cream hover:bg-ink-2"
                }`}
              >
                {t.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-rule mt-8">
        <div className="max-w-6xl mx-auto px-8 py-8 text-xs text-ink-mute flex items-center justify-between flex-wrap gap-2">
          <div>© Nomni Procure · An agent-native shell over Zeemart's buyer APIs.</div>
          <div>Not affiliated with Zeemart. Your Zeemart account is the source of truth.</div>
        </div>
      </footer>
    </main>
  );
}
